import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

const BLUE = '#2563eb';
const WHITE = '#ffffff';
const TEXT = '#0a0a0a';
const TEXT_MUTED = '#666666';
const BORDER = '#e5e7eb';

export default function Auth() {
  const isMobile = useIsMobile();
  const [mode, setMode] = useState('login'); // 'login' or 'signup'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  async function handleSubmit() {
    setError('');
    setSuccess('');
    setLoading(true);

    if (mode === 'signup') {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { full_name: fullName } }
      });
      if (error) {
        setError(error.message);
      } else {
        setSuccess('Account created! Check your email to confirm, then log in.');
      }
    } else {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        setError(error.message);
      } else {
        navigate(mode === 'signup' ? '/onboarding' : '/dashboard');
      }
    }
    setLoading(false);
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') handleSubmit();
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Background glow */}
      <div style={{
        position: 'absolute', top: '30%', left: '50%',
        transform: 'translateX(-50%)',
        width: 600, height: 600,
        background: 'radial-gradient(ellipse, rgba(37,99,235,0.08) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      <div style={{
        width: '100%', maxWidth: 420,
        background: WHITE,
        border: '1px solid ' + BORDER,
        borderRadius: 20,
        padding: isMobile ? '1.5rem' : '2.5rem',
        position: 'relative',
        boxShadow: '0 20px 50px rgba(15,23,42,0.08)',
        animation: 'fadeUp .4s ease both',
      }}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '2rem', justifyContent: 'center' }}>
          <div style={{
            width: 36, height: 36,
            background: BLUE,
            borderRadius: 10,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={WHITE} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
            </svg>
          </div>
          <span style={{ fontSize: 22, fontWeight: 800, color: TEXT }}>
            Pre<span style={{ color: BLUE }}>Visr</span>
          </span>
        </div>

        {/* Title */}
        <h1 style={{ fontSize: '1.6rem', fontWeight: 800, color: TEXT, textAlign: 'center', marginBottom: '0.4rem' }}>
          {mode === 'login' ? 'Welcome back' : 'Create your account'}
        </h1>
        <p style={{ color: TEXT_MUTED, fontSize: '0.875rem', textAlign: 'center', marginBottom: '2rem' }}>
          {mode === 'login' ? 'Log in to access your school list' : 'Start building your med school list'}
        </p>

        {/* Fields */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {mode === 'signup' && (
            <AuthField label="Full Name" value={fullName} onChange={setFullName} placeholder="Alex Chen" type="text" onKeyDown={handleKeyDown} />
          )}
          <AuthField label="Email" value={email} onChange={setEmail} placeholder="you@email.com" type="email" onKeyDown={handleKeyDown} />
          <AuthField label="Password" value={password} onChange={setPassword} placeholder="********" type="password" onKeyDown={handleKeyDown} />
        </div>

        {/* Error / Success */}
        {error && (
          <div style={{ marginTop: 14, padding: '10px 14px', background: '#fef2f2', border: '0.5px solid #fca5a5', borderRadius: 8, fontSize: '0.82rem', color: '#b91c1c' }}>
            {error}
          </div>
        )}
        {success && (
          <div style={{ marginTop: 14, padding: '10px 14px', background: '#f0fdf4', border: '0.5px solid #86efac', borderRadius: 8, fontSize: '0.82rem', color: '#15803d' }}>
            {success}
          </div>
        )}

        {/* Submit */}
        <button onClick={handleSubmit} disabled={loading} style={{
          width: '100%', marginTop: 20,
          padding: '13px',
          background: loading ? '#93b4f8' : BLUE,
          color: WHITE, fontWeight: 700, fontSize: '0.95rem',
          border: 'none', borderRadius: 10, cursor: loading ? 'wait' : 'pointer',
          boxShadow: loading ? 'none' : '0 4px 10px rgba(37,99,235,0.25)',
          transition: 'all .2s',
        }}>
          {loading ? 'Please wait...' : mode === 'login' ? 'Log In' : 'Create Account'}
        </button>

        {/* Toggle mode */}
        <p style={{ textAlign: 'center', marginTop: 20, fontSize: '0.875rem', color: TEXT_MUTED }}>
          {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
          <span onClick={() => { setMode(mode === 'login' ? 'signup' : 'login'); setError(''); setSuccess(''); }}
            style={{ color: BLUE, cursor: 'pointer', fontWeight: 600 }}>
            {mode === 'login' ? 'Sign up' : 'Log in'}
          </span>
        </p>
      </div>

      {/* Footer links */}
      <div style={{ display: 'flex', gap: 24, marginTop: 24, justifyContent: 'center', flexWrap: 'wrap' }}>
        <Link to="/guide" style={{ fontSize: 12, color: TEXT_MUTED, textDecoration: 'none', fontWeight: 500 }}
          onMouseEnter={e => e.currentTarget.style.color = BLUE}
          onMouseLeave={e => e.currentTarget.style.color = TEXT_MUTED}>
          Med School Guide
        </Link>
        <Link to="/about" style={{ fontSize: 12, color: TEXT_MUTED, textDecoration: 'none', fontWeight: 500 }}
          onMouseEnter={e => e.currentTarget.style.color = BLUE}
          onMouseLeave={e => e.currentTarget.style.color = TEXT_MUTED}>
          About PreVisr
        </Link>
        <Link to="/stats" style={{ fontSize: 12, color: TEXT_MUTED, textDecoration: 'none', fontWeight: 500 }}
          onMouseEnter={e => e.currentTarget.style.color = BLUE}
          onMouseLeave={e => e.currentTarget.style.color = TEXT_MUTED}>
          Stats
        </Link>
        <Link to="/privacy" style={{ fontSize: 12, color: TEXT_MUTED, textDecoration: 'none', fontWeight: 500 }}
          onMouseEnter={e => e.currentTarget.style.color = BLUE}
          onMouseLeave={e => e.currentTarget.style.color = TEXT_MUTED}>
          Privacy
        </Link>
        <Link to="/terms" style={{ fontSize: 12, color: TEXT_MUTED, textDecoration: 'none', fontWeight: 500 }}
          onMouseEnter={e => e.currentTarget.style.color = BLUE}
          onMouseLeave={e => e.currentTarget.style.color = TEXT_MUTED}>
          Terms
        </Link>
      </div>
    </div>
  );
}

function AuthField({ label, value, onChange, type, placeholder, onKeyDown }) {
  const [focused, setFocused] = useState(false);
  return (
    <div>
      <label style={{ fontSize: '0.75rem', fontWeight: 600, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '.05em', display: 'block', marginBottom: 6 }}>
        {label}
      </label>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onKeyDown={onKeyDown}
        style={{
          width: '100%', padding: '10px 13px',
          background: WHITE,
          border: `1px solid ${focused ? BLUE : BORDER}`,
          borderRadius: 9, color: TEXT, fontSize: '0.9rem',
          outline: 'none', transition: 'border-color .2s, box-shadow .2s',
          boxShadow: focused ? '0 0 0 3px rgba(37,99,235,.1)' : 'none',
        }}
      />
    </div>
  );
}
